
import { Trophy, Calendar, Quote } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { CertificateData } from "../CertificateDialog";

interface CertificateContentProps {
  data: CertificateData;
  style: Record<string, string>;
  userImageUrl: string | null;
}

export function CertificateContent({ data, style, userImageUrl }: CertificateContentProps) {
  return (
    <div className={cn(
      "text-center space-y-1 sm:space-y-2 md:space-y-4 my-1 sm:my-2 md:my-4 w-full max-w-2xl mx-auto",
      style.text,
      style.contentFont,
      "animate-fade-in"
    )} style={{ animationDelay: "200ms" }}>
      <h2 className={cn(
        "font-semibold",
        "text-sm sm:text-base md:text-xl"
      )}>
        This is to certify that
      </h2>
      <p className={cn(
        "font-bold",
        "text-base sm:text-2xl md:text-4xl",
        style.accent,
        "transition-all duration-300 hover:scale-105"
      )}>
        {data.userName}
      </p>
      <p className="text-xs sm:text-sm md:text-lg">
        has successfully achieved the goal
      </p>
      <p className={cn(
        "font-bold",
        "text-sm sm:text-xl md:text-2xl",
        style.accent,
        "transition-all duration-300"
      )}>
        {data.goalTitle}
      </p>
      
      {userImageUrl && (
        <div className="relative w-12 h-12 sm:w-20 sm:h-20 md:w-32 md:h-32 mx-auto mt-1 sm:mt-2 mb-2 sm:mb-4 animate-fade-in" style={{ animationDelay: "300ms" }}>
          <img
            src={userImageUrl}
            alt={`${data.userName}'s photo`}
            className={cn(
              "w-full h-full object-contain rounded-full border-2 sm:border-4 shadow-lg",
              style.border,
              "transition-all duration-300 hover:scale-105"
            )}
            loading="lazy"
          />
        </div>
      )}
      
      <div className="flex justify-center gap-2 sm:gap-4 md:gap-6 mt-1 sm:mt-2 md:mt-4 animate-fade-in" style={{ animationDelay: "400ms" }}>
        <div className="flex items-center gap-1 sm:gap-2">
          <Trophy className={cn("h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5", style.accent)} />
          <span className="text-xs sm:text-sm md:text-lg">Progress: {data.progress}%</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Calendar className={cn("h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5", style.accent)} />
          <span className="text-xs sm:text-sm md:text-lg">
            Completed on: {formatDate(data.dueDate)}
          </span>
        </div>
      </div>

      <div className="mt-2 sm:mt-4 md:mt-6 space-y-1 sm:space-y-2 animate-fade-in" style={{ animationDelay: "500ms" }}>
        <Quote className={cn("h-3 w-3 sm:h-4 sm:w-4 md:h-6 md:w-6 mx-auto", style.accent)} />
        <p className="text-[10px] sm:text-xs md:text-base italic">
          {data.tagline}
        </p>
      </div>
    </div>
  );
}
